import { useContext, useEffect, useRef } from "react";

import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Divider,
  useTheme
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HelpIcon from "@mui/icons-material/Help";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CloseIcon from "@mui/icons-material/Close";

import { hexToRgba } from "@aclymatepackages/converters";

import { SurveyContext } from "../survey-shape/SurveyContext";
import { getFieldDisplayValue } from "./scoutFieldHandlers";

const StatusIcon = ({ status, palette }) => {
  if (status === "captured") {
    return (
      <CheckCircleIcon
        fontSize="small"
        style={{ color: palette.secondary.main }}
      />
    );
  }
  if (status === "unknown") {
    return (
      <HelpIcon fontSize="small" style={{ color: palette.text.secondary }} />
    );
  }
  if (status === "skipped") {
    return (
      <SkipNextIcon
        fontSize="small"
        style={{ color: palette.text.secondary }}
      />
    );
  }
  return (
    <RadioButtonUncheckedIcon
      fontSize="small"
      style={{ color: palette.backgroundGray.dark }}
    />
  );
};

const STATUS_LABEL_MAP = {
  skipped: "Skipped",
  unknown: "Don't know",
  pending: "Answering now"
};

const SummaryRow = ({ entry, palette, onEdit }) => {
  const statusLabel = STATUS_LABEL_MAP[entry.status] || entry.value;
  const isInteractive = entry.status !== "pending";

  return (
    <Box
      px={2}
      py={1.5}
      display="flex"
      alignItems="center"
      style={{
        gap: 12,
        borderRadius: 8,
        backgroundColor: entry.isCurrent
          ? hexToRgba(palette.primary.light, 0.15)
          : "transparent",
        border: entry.isCurrent
          ? `1px solid ${palette.primary.main}`
          : "1px solid transparent"
      }}
    >
      <StatusIcon status={entry.status} palette={palette} />
      <Box flexGrow={1} minWidth={0} style={{ wordBreak: "break-word" }}>
        <Typography variant="caption" color="textSecondary">
          {entry.title}
        </Typography>
        <Typography
          variant="body2"
          color={entry.status === "captured" ? "textPrimary" : "textSecondary"}
        >
          {statusLabel}
        </Typography>
      </Box>
      {isInteractive && onEdit && (
        <Tooltip title="Edit this answer">
          <IconButton size="small" onClick={() => onEdit(entry.stepIndex)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

const SurveyCapturedSummary = ({
  surveyStepMeta = [],
  isMobileDrawer = false,
  onCloseMobile
}) => {
  const { palette } = useTheme();
  const context = useContext(SurveyContext);
  const {
    formStep,
    skippedFields = [],
    unknownFields = [],
    startEditingField,
    editingFromStep
  } = context;

  const computeStatus = (meta) => {
    const { fieldId } = meta;
    if (!fieldId) {
      return { status: "pending", value: null };
    }
    const value = getFieldDisplayValue(fieldId, context);
    if (value) {
      return { status: "captured", value };
    }
    if (skippedFields.includes(fieldId)) {
      return { status: "skipped", value: null };
    }
    if (unknownFields.includes(fieldId)) {
      return { status: "unknown", value: null };
    }
    return { status: "pending", value: null };
  };

  const entries = surveyStepMeta.map((meta) => {
    const { status, value } = computeStatus(meta);
    return {
      ...meta,
      value,
      status,
      isCurrent: meta.stepIndex === formStep
    };
  });

  const visibleEntries = entries.filter(
    (entry) => entry.status !== "pending" || entry.isCurrent
  );

  const scrollContainerRef = useRef(null);
  const currentEntryRef = useRef(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    const target = currentEntryRef.current;
    if (!container || !target) {
      return;
    }
    requestAnimationFrame(() => {
      const containerRect = container.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const isAbove = targetRect.top < containerRect.top;
      const isBelow = targetRect.bottom > containerRect.bottom;
      if (isAbove || isBelow) {
        target.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    });
  }, [formStep, visibleEntries.length]);

  const wrapperSx = isMobileDrawer
    ? { display: "flex", flex: "1 1 auto", width: "100%" }
    : {
        display: { xs: "none", md: "flex" },
        flex: "0 0 40%",
        maxWidth: "40%"
      };

  return (
    <Box
      sx={wrapperSx}
      flexDirection="column"
      style={{
        background: palette.backgroundGray.light,
        height: "100%"
      }}
    >
      <Box
        px={3}
        py={2}
        display="flex"
        alignItems="center"
        style={{
          borderBottom: `1px solid ${palette.backgroundGray.dark}`,
          gap: 12
        }}
      >
        <Box flexGrow={1}>
          <Typography variant="subtitle1" color="textPrimary">
            Your survey so far
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {editingFromStep !== null
              ? "Editing — Scout will return you when you're done."
              : "Tap the pencil on any answer to change it."}
          </Typography>
        </Box>
        {isMobileDrawer && onCloseMobile && (
          <IconButton size="small" onClick={onCloseMobile}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box
        ref={scrollContainerRef}
        flexGrow={1}
        px={2}
        py={1}
        display="flex"
        flexDirection="column"
        style={{ gap: 4, overflowY: "auto", minHeight: 0 }}
      >
        {visibleEntries.length === 0 && (
          <Typography variant="body2" color="textSecondary" align="center">
            Your answers will appear here as you go.
          </Typography>
        )}
        {visibleEntries.map((entry, idx) => (
          <Box
            key={`summary-${entry.stepIndex}-${entry.fieldId || idx}`}
            ref={entry.isCurrent ? currentEntryRef : null}
          >
            <SummaryRow
              entry={entry}
              palette={palette}
              onEdit={startEditingField}
            />
            {idx < visibleEntries.length - 1 && <Divider />}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
export default SurveyCapturedSummary;
